import { useAppDispatch, useAppSelector } from 'appState/store'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { IUser } from 'features/auth/types'
import { AutomationTypeEnum, IBulkCalls } from 'features/calls/types'
import { bulkOwner, startBulkCalls } from 'features/claims/claimsApi'
import { refreshClaimCount } from 'features/claims/claimsSlice'
import { getLongAgentName } from 'features/claims/utils'
import { WellKnownActorsEnum, wellKnownEntityIDs } from 'features/entities/types'
import { useState } from 'react'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'

import { UNASSIGNED } from '../../ClaimDetails/Internal/Constants'
import { isSelectedClaimIsOwnedByCurrentUser } from './ClaimsTableHelper'
import { BulkType } from './ClaimsTableTypes'

export interface IProps {
  setShow: (value?: string) => void
  type: BulkType
  selectedRow: any
  oaiUserName?: string | null
  oaiUserId?: string | null
  GridApi: any
}

const modalTitle = (claimsCount: number, type: BulkType, userName?: string) => {
  const titleText = {
    unAssign: 'Unassign all selected claim(s)?',
    takeOwnership: `Assign all selected claim(s) to ${userName}?`,
    fetchEbv: 'Fetch EBV for Multiple Claims?',
    fetchECS: 'Fetch ECS for Multiple Claims?',
    fetchEOB: 'Fetch EOB for Multiple Claims?',
    fetchASC: 'Fetch ASC for Multiple Claims?',
  }
  const getContentForType = titleText[type]
  return getContentForType
}

const modalButtonText = (claimsCount: number, type: BulkType) => {
  const buttonText = {
    unAssign: 'Yes, unassign',
    takeOwnership: `Yes, assign claims`,
    fetchEbv: 'Yes, Fetch EBV',
    fetchECS: 'Yes, Fetch ECS',
    fetchEOB: 'Yes, Fetch EOB',
    fetchASC: 'Yes, Fetch ASC',
  }
  const getContentForType = buttonText[type]
  return getContentForType
}
function modalContent(claimsCount: number, type: BulkType, userName?: string) {
  const mContent = {
    unAssign: (
      <>
        The current assignee(s) will no longer be able to initiate calls on the claim(s). <br /> Please note: any claims
        on active calls will not be unassigned.
      </>
    ),
    takeOwnership: (
      <>
        This will allow the assignee to initiate calls on the claim(s). <br /> Please note: any claims on active calls
        or claims already assigned to <span> {userName}</span> will not be reassigned.
      </>
    ),
    fetchEbv: `Would you like to proceed to Fetch EBV for the ${claimsCount} eligible rows? This action cannot be undone.`,
    fetchECS: `Would you like to proceed to Fetch ECS for the ${claimsCount} eligible rows? This action cannot be undone.`,
    fetchEOB: `Would you like to proceed to Fetch EOB for the ${claimsCount} eligible rows? This action cannot be undone.`,
    fetchASC: `Would you like to proceed to Fetch ASC for the ${claimsCount} eligible rows? This action cannot be undone.`,
  }

  const getContentForType = mContent[type]
  return getContentForType
}

export const BulkActionConfirmationModal = ({
  setShow,
  type,
  selectedRow,
  oaiUserName,
  oaiUserId,
  GridApi,
}: IProps) => {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const currentUser: IUser = storage.get(StorageEnum.currentUser)
  const userId = currentUser.id
  const getClaimsOwnedByCurrentUser = (ownerId: string, type?: BulkType) => {
    const selectedRows = GridApi && GridApi.api.getSelectedRows() ? GridApi.api.getSelectedRows() : []
    const claimsOwnedByCurrentUser = selectedRows.filter((claim: any) => claim?.ownerId === ownerId)
    let AutomationType: AutomationTypeEnum[]

    if (type === 'fetchEbv') {
      AutomationType = [AutomationTypeEnum.los1, AutomationTypeEnum.los2, AutomationTypeEnum.los4]
    } else if (type === 'fetchECS') {
      AutomationType = [
        AutomationTypeEnum.fullCSI,
        AutomationTypeEnum.los1,
        AutomationTypeEnum.los4,
        AutomationTypeEnum.los5,
      ]
    } else if (type === 'fetchEOB') {
      AutomationType = [AutomationTypeEnum.los4, AutomationTypeEnum.los5, AutomationTypeEnum.los6]
    } else if (type === 'fetchASC') {
      AutomationType = [AutomationTypeEnum.los9, AutomationTypeEnum.los10, AutomationTypeEnum.los11, AutomationTypeEnum.ASC]
    } else {
      AutomationType = []
    }

    if (!AutomationType || AutomationType.length === 0) {
      return claimsOwnedByCurrentUser
    }

    return claimsOwnedByCurrentUser.filter((claim: any) => {
      return AutomationType.some((type: any) => claim?.levelOfSupport?.includes(type))
    })
  }
  const currentUserClaims = getClaimsOwnedByCurrentUser(userId!, type)

  let totalClaimsCount = 0

  if (type === 'takeOwnership') {
    totalClaimsCount = isSelectedClaimIsOwnedByCurrentUser(selectedRow, userId!).length
  } else {
    totalClaimsCount = currentUserClaims?.length
  }

  const actorName = getLongAgentName(user)
  const [isLoading, setLoading] = useState(false)

  const fetchStatus = () => {
    setLoading(true)

    const fetchStatus: IBulkCalls = {
      forceOwnership: false,
      data: selectedRow.map((claim: any) => {
        return {
          claimId: claim.oaiClaimId,
          useCase: 1,
        }
      }),
    }

    startBulkCalls(fetchStatus)
      .then((response) => {
        dispatch(refreshClaimCount({ refreshCount: true }))
        GridApi.api.refreshServerSide({ purge: true })
        setShow('CLEAR')
        setLoading(false)
        GridApi.api.deselectAll()
        notificator.notify({
          heading: `Successfully queued ${response.data.length} claim(s) to Fetch ECS  `,
          type: 'success',
        })
        /* notificator.success(
          `Successfully queued ${response.data.length} ${pluralMessage(response.data.length, 'claim')} to Fetch ECS  `,
        ) */
      })
      .catch((error) => {
        notificator.notify({
          heading: error.response?.data?.detail || error.message,
          type: 'error',
        })
        //notificator.error(error.response?.data?.detail || error.message)
        setShow('CLEAR')
        setLoading(false)
      })
  }

  const takeOwnership = () => {
    if (type === 'takeOwnership' && (!oaiUserName || !oaiUserId)) {
      notificator.notify({
        heading: 'Selected user id is undefined',
        type: 'error',
      })
      return
    }
    setLoading(true)
    const bulkOwnership = {
      claims: selectedRow.map((claim: any) => {
        const prevUserName = claim?.owner ? claim.owner : UNASSIGNED
        return {
          oaiClaimId: claim.oaiClaimId,
          activityId: '3dca2d43-aaf1-4e36-8636-22c706d66146',
          actorName: actorName,
          actorId: WellKnownActorsEnum.rcmAgent,
          // remarks: (oaiUserName && oaiUserId) === null ? 'Release ownerships' : 'Claims ownership',
          remarks: getRemarksOwnership(prevUserName, actorName, oaiUserName ? oaiUserName : UNASSIGNED),
          entities: {
            [wellKnownEntityIDs.ownerName]: oaiUserName,
            [wellKnownEntityIDs.ownerId]: oaiUserId,
          },
        }
      }),
    }
    bulkOwner(bulkOwnership)
      .then((res: any) => {
        if (res) {
          GridApi.api.refreshServerSide({ purge: true })
          notificator.notify({
            heading: `Successfully ${oaiUserName ? 'assigned' : 'unassigned'} ${res.length} claim(s)`,
            type: 'success',
          })
          //notificator.success(`Successfully assigned ${res.length} ${pluralMessage(res.length, 'claim')}`)
          setShow()
          setLoading(false)
        }
      })
      .catch((err: any) => {
        console.log('🚀 ~ takeOwnership ~ err:', err)
        const statusCode = err?.response?.data?.status
        const errorMessage = err?.response?.data.detail?.toLocaleLowerCase()
        const isNotUpdated = errorMessage === 'no claims were updated.'
        setShow()
        setLoading(false)
        if (statusCode === 417 && isNotUpdated) {
          notificator.notify({
            heading: 'Some or all selected claims were not reassigned',
            message:
              'The claim(s) you selected might be on an active call or were already assigned to the selected user.',
            type: 'info',
          })
        } else {
          notificator.notify({
            heading: err.response?.data?.detail || err.message,
            type: 'error',
          })
        }
      })
  }

  const fetchEBV = () => {
    setLoading(true)
    const fetchStatus: IBulkCalls = {
      forceOwnership: false,
      data: selectedRow.map((claim: any) => {
        return {
          claimId: claim.oaiClaimId,
          useCase: 2,
        }
      }),
    }

    startBulkCalls(fetchStatus)
      .then((response) => {
        dispatch(refreshClaimCount({ refreshCount: true }))
        GridApi.api.refreshServerSide({ purge: true })
        setShow('CLEAR')
        setLoading(false)
        GridApi.api.deselectAll()
        notificator.notify({
          heading: `Successfully queued ${response.data.length} claim(s) to Fetch EBV`,
          type: 'success',
        })
        /* notificator.success(
          `Successfully queued ${response.data.length} ${pluralMessage(response.data.length, 'claim')} to Fetch EBV`,
        ) */
      })
      .catch((error) => {
        setShow('CLEAR')
        setLoading(false)
        notificator.notify({
          heading: error.response?.data?.detail || error.message,
          type: 'error',
        })
        //notificator.error(error.response?.data?.detail || error.message)
      })
  }

  const fetchEOB = () => {
    setLoading(true)
    const fetchStatus: IBulkCalls = {
      forceOwnership: false,
      data: selectedRow.map((claim: any) => {
        return {
          claimId: claim.oaiClaimId,
          useCase: 3,
        }
      }),
    }

    startBulkCalls(fetchStatus)
      .then((response) => {
        dispatch(refreshClaimCount({ refreshCount: true }))
        GridApi.api.refreshServerSide({ purge: true })
        setShow('CLEAR')
        setLoading(false)
        GridApi.api.deselectAll()
        notificator.notify({
          heading: `Successfully queued ${response.data.length} claim(s) for Fetch ECS and/or EOB`,
          type: 'success',
        })
        /* notificator.success(
          `Successfully queued ${response.data.length} ${pluralMessage(response.data.length, 'claim')} to Fetch EOB`,
        ) */
      })
      .catch((error) => {
        setShow('CLEAR')
        setLoading(false)
        notificator.notify({
          heading: error.response?.data?.detail || error.message,
          type: 'error',
        })
        //notificator.error(error.response?.data?.detail || error.message)
      })
  }

  const fetchASC = () => {
    setLoading(true)
    const fetchStatus: IBulkCalls = {
      forceOwnership: false,
      data: selectedRow.map((claim: any) => {
        return {
          claimId: claim.oaiClaimId,
          useCase: 7,
        }
      }),
    }

    startBulkCalls(fetchStatus)
      .then((response) => {
        dispatch(refreshClaimCount({ refreshCount: true }))
        GridApi.api.refreshServerSide({ purge: true })
        setShow('CLEAR')
        setLoading(false)
        GridApi.api.deselectAll()
        notificator.notify({
          heading: `Successfully queued ${response.data.length} claim(s) for Fetch ASC`,
          type: 'success',
        })
      })
      .catch((error) => {
        setShow('CLEAR')
        setLoading(false)
        notificator.notify({
          heading: error.response?.data?.detail || error.message,
          type: 'error',
        })
      })
  }

  const handleBulkAction = () => {
    switch (type) {
      case 'unAssign':
        takeOwnership()
        break
      case 'takeOwnership':
        takeOwnership()
        break
      case 'fetchEbv':
        fetchEBV()
        break
      case 'fetchECS':
        fetchStatus()
        break
      case 'fetchEOB':
        fetchEOB()
        break
      case 'fetchASC':
        fetchASC()
        break
    }
  }

  return (
    <ObModal
      show={true}
      close={() => setShow()}
      title={modalTitle(totalClaimsCount, type, oaiUserName || '')}
      modalFooter={
        <div style={FooterStyle}>
          <Button
            style={BtnStyle}
            disabled={isLoading}
            className="me-3"
            variant="primary-outline"
            radius="rounded"
            size="large"
            onClick={handleBulkAction}
          >
            {modalButtonText(totalClaimsCount, type)}
            {isLoading && <LoadingAnimation />}
          </Button>
          <Button onClick={() => setShow()} style={BtnStyle} radius="rounded" size="large">
            No, cancel
          </Button>
        </div>
      }
    >
      <ContentDiv>{modalContent(totalClaimsCount, type, oaiUserName || '')}</ContentDiv>
    </ObModal>
  )
}

export function getRemarksOwnership(currentOwner: string, currentLoggedInUser: string, assignedOwner: string) {
  if (currentLoggedInUser === assignedOwner) {
    return `took ownership of this claim`
  }
  if (currentLoggedInUser === currentOwner && assignedOwner === UNASSIGNED) {
    return `unassigned this claim`
  }
  if (currentLoggedInUser !== currentOwner && assignedOwner === UNASSIGNED) {
    return `unassigned this claim from ${currentOwner}`
  }
  if (currentLoggedInUser !== assignedOwner && assignedOwner !== UNASSIGNED) {
    return `assigned this claim to ${assignedOwner}`
  }
  return 'changed claim ownership'
}

const FooterStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  margin: 0,
  width: '100%',
}
const BtnStyle = {
  width: '100%',
}

const ContentDiv = styled.div`
  text-align: left;
  /* margin: 0 auto; */
  font-family: Milliard;
  .content-title {
    font-size: 1.125rem;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    color: ${({ theme }) => theme.colors.neutral.neutral};
    margin-top: 1.5625rem;
    margin-bottom: 0.375rem;
  }
  p {
    font-size: 1rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: ${({ theme }) => theme.colors.grey.dark};
  }
`
